/**
 * job_type_validation_rules — OPEN-217 / DEC-382 (migration 0266). A hauler's rules on what a
 * driver types into a ticket, per job type FAMILY: family_id has no FK, as on
 * job_type_customers, because family_id alone is not unique across versions. See the
 * migration header for the rules and shared validation-rules.ts for the catalogue and the
 * evaluator; the API reads and writes it with raw SQL, and this mirror keeps schema-diff
 * tooling aligned.
 */

import { sql } from 'drizzle-orm';
import {
  boolean,
  check,
  index,
  integer,
  numeric,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  uuid,
} from 'drizzle-orm/pg-core';
import { appUserRole } from './enums.js';
import { serviceCharges } from './service-charges.js';
import { tenants, users } from './users.js';

export const jobTypeValidationRules = pgTable(
  'job_type_validation_rules',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    familyId: uuid('family_id').notNull(),
    field: text('field').notNull(),
    checkKind: text('check_kind').notNull(),
    minValue: numeric('min_value', { precision: 12, scale: 3 }),
    maxValue: numeric('max_value', { precision: 12, scale: 3 }),
    message: text('message').notNull(),
    severity: text('severity').notNull(),
    serviceChargeId: uuid('service_charge_id').references(() => serviceCharges.id, {
      onDelete: 'cascade',
    }),
    isActive: boolean('is_active').notNull().default(true),
    sortOrder: integer('sort_order').notNull().default(0),
    createdBy: uuid('created_by').references(() => users.id, { onDelete: 'set null' }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedBy: uuid('updated_by').references(() => users.id, { onDelete: 'set null' }),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    check(
      'job_type_validation_rules_field_check',
      sql`${t.field} IN ('TANK_WATER_BBL','TANK_OIL_BBL','TANK_TOP_GAUGE_IN','TANK_BOTTOM_GAUGE_IN','TANK_GAUGE_CHANGE_IN','TANK_BEFORE_BBL','TANK_AFTER_BBL','TANK_NET_BBL','TICKET_NET_BBL','TANK_COUNT','LINE_ITEM_QTY','SERVICE_QTY','DRIVER_TIME_MIN')`,
    ),
    check(
      'job_type_validation_rules_check_kind_check',
      sql`${t.checkKind} IN ('GT','LT','BETWEEN','REQUIRED')`,
    ),
    check('job_type_validation_rules_severity_check', sql`${t.severity} IN ('WARNING','BLOCK')`),
    check(
      'job_type_validation_rules_bounds_shape',
      sql`(check_kind = 'GT' AND min_value IS NOT NULL AND max_value IS NULL)
        OR (check_kind = 'LT' AND min_value IS NULL AND max_value IS NOT NULL)
        OR (check_kind = 'BETWEEN' AND min_value IS NOT NULL AND max_value IS NOT NULL
            AND min_value <= max_value)
        OR (check_kind = 'REQUIRED' AND min_value IS NULL AND max_value IS NULL)`,
    ),
    check(
      'job_type_validation_rules_message_length',
      sql`length(btrim(message)) > 0 AND length(message) <= 200`,
    ),
    check(
      'job_type_validation_rules_service_charge_shape',
      sql`(field = 'SERVICE_QTY') = (service_charge_id IS NOT NULL)`,
    ),
    index('job_type_validation_rules_family_idx').on(t.tenantId, t.familyId),
    index('job_type_validation_rules_service_charge_idx')
      .on(t.serviceChargeId)
      .where(sql`service_charge_id IS NOT NULL`),
    pgPolicy('job_type_validation_rules_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
      withCheck: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type JobTypeValidationRuleRow = typeof jobTypeValidationRules.$inferSelect;
